import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { apiClient } from '../api/client';

const emptyAdd = { identifier: '', name: '', kind: 'evccId', authorize: true };

const Fleet = () => {
  const { addToast } = useToast();
  const api = apiClient;
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');
  const [drafts, setDrafts] = useState({});
  const [addForm, setAddForm] = useState(emptyAdd);

  const load = useCallback(async () => {
    const resp = await api.get('/vehicles');
    const rows = resp.data.vehicles || [];
    setVehicles(rows);
    const next = {};
    rows.forEach((v) => {
      next[v.identifier] = {
        name: v.name || '',
        notes: v.notes || '',
        kind: v.kind || 'idTag',
        authorize: Boolean(v.authorize),
      };
    });
    setDrafts(next);
    return rows;
  }, [api]);

  useEffect(() => {
    load()
      .catch((err) => addToast('Failed to load fleet: ' + (err.response?.data?.detail || err.message), 'error'))
      .finally(() => setLoading(false));
  }, [load, addToast]);

  const save = async (identifier, extra = {}) => {
    const draft = { ...(drafts[identifier] || {}), ...extra };
    setSavingId(identifier);
    try {
      await api.put(`/vehicles/${encodeURIComponent(identifier)}`, {
        name: (draft.name || '').trim() || null,
        notes: (draft.notes || '').trim() || null,
        kind: draft.kind || 'idTag',
        authorize: Boolean(draft.authorize),
      });
      addToast(`Saved ${identifier}`, 'success');
      await load();
    } catch (err) {
      addToast('Save failed: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setSavingId('');
    }
  };

  const toggleAuthorize = async (row) => {
    const next = !Boolean((drafts[row.identifier] || row).authorize);
    setDrafts((prev) => ({
      ...prev,
      [row.identifier]: { ...(prev[row.identifier] || {}), authorize: next },
    }));
    await save(row.identifier, { authorize: next });
  };

  const remove = async (identifier) => {
    if (!window.confirm(`Remove ${identifier} from the fleet list? Authorize will stop.`)) return;
    try {
      await api.delete(`/vehicles/${encodeURIComponent(identifier)}`);
      addToast('Removed ' + identifier, 'success');
      await load();
    } catch (err) {
      addToast('Delete failed: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const addVehicle = async (e) => {
    e.preventDefault();
    const ident = (addForm.identifier || '').trim();
    if (!ident) {
      addToast('Identifier is required', 'error');
      return;
    }
    if (ident.length > 20) {
      addToast('OCPP idTag max 20 characters (MACs are 12 hex)', 'error');
      return;
    }
    setSavingId(ident);
    try {
      await api.put(`/vehicles/${encodeURIComponent(ident)}`, {
        name: (addForm.name || '').trim() || null,
        kind: addForm.kind,
        authorize: Boolean(addForm.authorize),
      });
      addToast('Added ' + ident, 'success');
      setAddForm(emptyAdd);
      await load();
    } catch (err) {
      addToast('Add failed: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setSavingId('');
    }
  };

  const setDraft = (identifier, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [identifier]: { ...(prev[identifier] || {}), [field]: value },
    }));
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Fleet IDs</h2>
      <p style={{ color: '#555', maxWidth: 820 }}>
        Every RFID / EVCC MAC the chargers report lands here. <strong>Authorize</strong> Accepted
        lets Sicon start by itself after plug-in. Turn Authorize off for a known ID if that car
        must not start. We do not send RemoteStart automatically. Unknown IDs stay blocked.
      </p>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginTop: 0 }}>Add ID</h3>
        <form onSubmit={addVehicle} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            maxLength={20}
            placeholder="MAC / RFID (max 20)"
            value={addForm.identifier}
            onChange={(e) => setAddForm({ ...addForm, identifier: e.target.value })}
            style={{ padding: '0.4rem 0.5rem', border: '1px solid #ddd', borderRadius: 4, minWidth: 180 }}
          />
          <input
            type="text"
            maxLength={128}
            placeholder="Fleet name"
            value={addForm.name}
            onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
            style={{ padding: '0.4rem 0.5rem', border: '1px solid #ddd', borderRadius: 4, minWidth: 180 }}
          />
          <select
            value={addForm.kind}
            onChange={(e) => setAddForm({ ...addForm, kind: e.target.value })}
            style={{ padding: '0.4rem 0.5rem', border: '1px solid #ddd', borderRadius: 4 }}
          >
            <option value="evccId">EVCC MAC</option>
            <option value="idTag">RFID / idTag</option>
            <option value="vin">VIN</option>
          </select>
          <label style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <input
              type="checkbox"
              checked={addForm.authorize}
              onChange={(e) => setAddForm({ ...addForm, authorize: e.target.checked })}
            />
            Authorize
          </label>
          <button type="submit" className="btn btn-primary" disabled={savingId === addForm.identifier}>
            Add
          </button>
        </form>
      </div>

      <div className="card">
        {loading ? (
          <p>Loading fleet…</p>
        ) : vehicles.length === 0 ? (
          <p style={{ color: '#666' }}>No IDs yet. Plug a car (VehicleMac) or add one above.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr>
                  <th style={th}>ID</th>
                  <th style={th}>Name</th>
                  <th style={th}>Kind</th>
                  <th style={th}>Authorize</th>
                  <th style={th}>Last seen</th>
                  <th style={th} />
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v) => {
                  const draft = drafts[v.identifier] || {};
                  return (
                    <tr key={v.identifier}>
                      <td style={td}>
                        <code>{v.identifier}</code>
                      </td>
                      <td style={td}>
                        <input
                          type="text"
                          value={draft.name || ''}
                          onChange={(e) => setDraft(v.identifier, 'name', e.target.value)}
                          placeholder="Fleet name"
                          style={{ width: '100%', minWidth: 140, padding: '0.3rem 0.4rem', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' }}
                        />
                      </td>
                      <td style={td}>
                        <select
                          value={draft.kind || 'idTag'}
                          onChange={(e) => setDraft(v.identifier, 'kind', e.target.value)}
                          style={{ padding: '0.3rem 0.4rem', border: '1px solid #ddd', borderRadius: 4 }}
                        >
                          <option value="evccId">EVCC MAC</option>
                          <option value="idTag">RFID / idTag</option>
                          <option value="vin">VIN</option>
                        </select>
                      </td>
                      <td style={td}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <input
                            type="checkbox"
                            checked={Boolean(draft.authorize)}
                            onChange={() => toggleAuthorize(v)}
                            disabled={savingId === v.identifier}
                          />
                          {draft.authorize ? 'on' : 'off'}
                        </label>
                      </td>
                      <td style={td}>
                        {v.last_charger_id ? (
                          <span>
                            <Link to={`/charger/${encodeURIComponent(v.last_charger_id)}`}>{v.last_charger_id}</Link>
                            {v.last_connector_id != null ? ` C${v.last_connector_id}` : ''}
                            <div style={{ fontSize: '0.75rem', color: '#666' }}>
                              {v.last_seen_at ? new Date(v.last_seen_at).toLocaleString() : ''}
                              {v.last_source ? ` · ${v.last_source}` : ''}
                            </div>
                          </span>
                        ) : (
                          <span style={{ color: '#888' }}>—</span>
                        )}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => save(v.identifier)}
                          disabled={savingId === v.identifier}
                          style={{ marginRight: '0.4rem' }}
                        >
                          {savingId === v.identifier ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" className="btn" onClick={() => remove(v.identifier)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const th = { textAlign: 'left', borderBottom: '1px solid #ddd', padding: '0.45rem 0.5rem' };
const td = { borderBottom: '1px solid #f0f0f0', padding: '0.45rem 0.5rem', verticalAlign: 'top' };

export default Fleet;
